import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Modal,
  Button,
  Form,
  Card,
  CardBody,
  Spinner,
} from "react-bootstrap";

import { getAllEmailTemplates, getInvoice, sendEmail } from "../../../api";
import { APIClient } from "../../../api/apiCore";
import { useParams } from "react-router-dom";

import BeatLoader from "react-spinners/BeatLoader";
import { useSnackbar } from "../../../utils/snackbar";

import moment from "moment";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { ClientDataType } from "../../../data/client";

interface OptionType {
  label: string;
  value: string;
}

interface EmailTemplateType {
  id: string;
  email_template_title: string;
  cc_recipients: string;
  message: string;
  subject: string;
}

interface InvoiceItem {
  id: number | string;
  item?: string;
  service_product_number?: string;
  description?: string;
  qty?: string;
  invoice_type?: string;
  unit_type?: string | number | null;
  unit_price?: string;
  selected_date?: Date | null;
  vat_type?: OptionType | null;
  total_cost_net?: string | number;
  total_cost_gross?: string | number;
  is_add_discount?: boolean;
  discount_type?: string;
  discount_price?: string | number;
  discount_percentage?: string | number;
  discounted_total_price_net?: string | number;
  discounted_total_price_gross?: string | number;
}

interface InvoiceData {
  id: string;
  items?: InvoiceItem[];
  customer: ClientDataType;
  client: { client_name: string; id: string };
  invoice_date: Date | null;
  due_date: Date | null | undefined;
  invoice_number: string;
  po_number: string;
  isChecked?: boolean;
}

const EmailInvoice: React.FC = () => {
  const { id } = useParams();
  const quillRef = useRef<ReactQuill | null>(null);
  const showSnackbar = useSnackbar();

  const [loading, setLoading] = useState<boolean>(false);
  const [emailSendLoading, setEmailSendLoading] = useState<boolean>(false);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateType[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateType>();
  const [invoiceData, setInvoiceData] = useState<InvoiceData>();

  const [subject, setSubject] = useState<string>("");
  const [message, setMessage] = useState<string>("");

  const [editorHeight, setEditorHeight] = useState(120);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const apiClient = new APIClient();

  useEffect(() => {
    const quillInstance = quillRef.current?.getEditor();
    if (quillInstance) {
      quillInstance.on("text-change", () => {
        const scrollHeight = quillInstance.root.scrollHeight;
        setEditorHeight(scrollHeight + 20);
      });
    }
  }, []);

  useEffect(() => {
    const fetchEmailTemplates = async () => {
      try {
        const response: any = await getAllEmailTemplates();
        setEmailTemplates(response.data);

        const payload_invoice = {
          invoiceId: id,
        };

        const response_invoice: any = await getInvoice(payload_invoice);
        setInvoiceData(response_invoice.data);
      } catch (error: any) {}
    };

    fetchEmailTemplates();
  }, []);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const response = (await apiClient.get(
          "/api/invoice/generate-invoice-pdf/" + id,
          {},
          "blob",
        )) as Blob;
        const url = URL.createObjectURL(response);
        setPdfUrl(url);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching invoice:", error);
        setLoading(false);
      }
    };

    fetchInvoice();
  }, []);

  const handleSelectEmailTemplate = (id: string) => {
    if (id != "0") {
      const selectedTemplate = emailTemplates.find(
        template => template.id === id,
      );
      setSubject(selectedTemplate?.subject ?? "");
      setMessage(selectedTemplate?.message ?? "");

      if (invoiceData) {
        replaceContent(
          selectedTemplate?.subject,
          selectedTemplate?.message,
          invoiceData,
        );
      }
    } else {
      setSubject("");
      setMessage("");
    }
  };

  const replaceContent = (
    subject?: string,
    message?: string,
    invoiceData?: InvoiceData,
  ) => {
    try {
      let replacedSubject = (subject ?? "")
        .replace(
          /{{\$customer_company_name}}/g,
          invoiceData?.customer?.company_name ?? "{{$customer_company_name}}",
        )
        .replace(
          /{{\$customer_full_name}}/g,
          invoiceData?.customer?.name_invoice_recipient ??
            "{{$customer_full_name}}",
        )
        .replace(
          /{{\$issue_date}}/g,
          moment(invoiceData?.invoice_date).format("DD/MM/yyyy") ??
            "{{$issue_date}}",
        )
        .replace(
          /{{\$due_date}}/g,
          invoiceData?.due_date
            ? moment(invoiceData?.due_date).format("DD/MM/yyyy")
            : "{{$due_date}}",
        )
        .replace(
          /{{\$invoice_number}}/g,
          invoiceData?.invoice_number ?? "{{$invoice_number}}",
        )
        .replace(
          /{{\$today_date}}/g,
          moment(new Date()).format("DD/MM/yyyy") ?? "{{$today_date}}",
        );

      setSubject(replacedSubject);

      let replacedMessage = (message ?? "")
        .replace(
          /{{\$customer_company_name}}/g,
          invoiceData?.customer?.company_name ?? "{{$customer_company_name}}",
        )
        .replace(
          /{{\$customer_full_name}}/g,
          invoiceData?.customer?.name_invoice_recipient ??
            "{{$customer_full_name}}",
        )
        .replace(
          /{{\$issue_date}}/g,
          moment(invoiceData?.invoice_date).format("DD/MM/yyyy") ??
            "{{$issue_date}}",
        )
        .replace(
          /{{\$due_date}}/g,
          invoiceData?.due_date
            ? moment(invoiceData?.due_date).format("DD/MM/yyyy")
            : "{{$due_date}}",
        )
        .replace(
          /{{\$invoice_number}}/g,
          invoiceData?.invoice_number ?? "{{$invoice_number}}",
        )
        .replace(
          /{{\$today_date}}/g,
          moment(new Date()).format("DD/MM/yyyy") ?? "{{$today_date}}",
        );

      setMessage(replacedMessage);
    } catch (error: any) {}
  };

  const handleSendEmail = async () => {
    try {
      setEmailSendLoading(true);
      const payload = {
        invoiceId: id,
        subject: subject,
        message: message,
      };

      const response = await sendEmail(payload);
      console.log(response);
      if (response?.success) {
        showSnackbar(response?.message, "info");
      } else {
        if (response?.status == 500) {
          showSnackbar(response?.message, "error");
        } else {
          showSnackbar(
            "Too many requests, please try again after an hour.",
            "error",
          );
        }
      }

      setEmailSendLoading(false);
    } catch (error: any) {
      if (error?.status == 429) {
        showSnackbar(
          "Too many requests, please try again after an hour.",
          "error",
        );
      } else {
        showSnackbar(error?.message, "error");
      }
      setEmailSendLoading(false);
    }
  };

  const handleChangeSubject = (e: any) => {
    setSubject(e.target.value);
  };

  const handleChangeMessage = (e: any) => {
    if (e?.target?.value) {
      setMessage(e.target.value);
    }
  };

  return (
    <div className="right-panel">
      <div className="sticky-top bg-white shadow-sm w-100 p-3 d-flex justify-content-between align-items-center z-index-100">
        <span className="mb-0 default-text" style={{ fontWeight: "500" }}>
          Email Invoice
        </span>
        <Button
          variant="primary"
          onClick={handleSendEmail}
          disabled={emailSendLoading}
        >
          Send
          {emailSendLoading && (
            <Spinner animation="border" size="sm" className="ms-2" />
          )}
        </Button>
      </div>

      <div className="py-3  px-4">
        <div id="breadcrumb" className="d-flex gap-2 my-3 mb-4">
          <i className="bx bx-home font-size-22"></i>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="flex-shrink-0 h-5 w-5"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
          <span>
            <a style={{ fontWeight: "500" }}> E-Invoices</a>
          </span>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="flex-shrink-0 h-5 w-5"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>

          <span>
            <a style={{ fontWeight: "500" }}> Email Invoice</a>
          </span>
        </div>
        <Row className="pt-3">
          <Col lg={4}>
            <Card>
              <CardBody style={{ minHeight: `${editorHeight}px` }}>
                <Form.Group controlId="">
                  <Form.Select
                    aria-label="Select gender"
                    onChange={(e: any) => {
                      handleSelectEmailTemplate(e.target.value);
                    }}
                  >
                    <option key="0" value="0">
                      Select Template{" "}
                    </option>
                    {emailTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.email_template_title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Label className="mt-4">Subject </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={subject}
                  onChange={handleChangeSubject}
                />

                <Form.Group>
                  <Form.Label className="mt-4">Message </Form.Label>
                  <ReactQuill
                    placeholder=""
                    onChange={handleChangeMessage}
                    value={message}
                    ref={quillRef}
                    style={{
                      minHeight: `${editorHeight}px`,
                      transition: "height 0.2s ease",
                    }}
                  />
                </Form.Group>
              </CardBody>
            </Card>
          </Col>
          <Col lg={8}>
            {pdfUrl && !loading && (
              <iframe
                src={pdfUrl}
                style={{ width: "100%", height: "1000px", border: "none" }}
              ></iframe>
            )}

            {!pdfUrl && loading && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "500px",
                }}
              >
                <BeatLoader color="gray" size={10} />
              </div>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default EmailInvoice;
